import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [balance, setBalance] = useState(0);
  const [profile, setProfile] = useState(null);
  const [myPlansData, setMyPlansData] = useState({
    activePlans: [],
    completedPlans: [],
    stats: {}
  });
  const [loading, setLoading] = useState(true);
  const [todayEarnings, setTodayEarnings] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showUpdateBanner, setShowUpdateBanner] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    fetchDashboardData();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [balanceRes, profileRes, myPlansRes] = await Promise.all([
        axios.get('https://builtfuturelife.com/api/balance', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://builtfuturelife.com/api/profile', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://builtfuturelife.com/api/my-plans', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setBalance(balanceRes.data.balance || 0);
      setProfile(profileRes.data);
      
      if (myPlansRes.data.success) {
        setMyPlansData({
          activePlans: myPlansRes.data.activePlans || [],
          completedPlans: myPlansRes.data.completedPlans || [],
          stats: myPlansRes.data.stats || {}
        });
        
        const todayEarnings = calculateTodayEarnings(myPlansRes.data.activePlans || []);
        setTodayEarnings(todayEarnings);
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTodayEarnings = (activePlans) => {
    if (!activePlans || !Array.isArray(activePlans)) return 0;
    
    return activePlans.reduce((total, plan) => {
      const dailyEarnings = plan.returns?.dailyAmount || 
                          plan.dailyIncomeAmount || 
                          (plan.hourlyIncome || 0) * 24 || 0;
      return total + dailyEarnings;
    }, 0);
  };

  const calculateTotalEarnings = () => {
    if (myPlansData.stats && myPlansData.stats.totalEarned !== undefined) {
      return myPlansData.stats.totalEarned;
    }
    
    const allPlans = [...myPlansData.activePlans, ...myPlansData.completedPlans];
    return allPlans.reduce((total, plan) => {
      return total + (plan.returns?.earnedSoFar || plan.earningsSoFar || 0);
    }, 0);
  };

  const calculateTotalInvested = () => {
    if (myPlansData.stats && myPlansData.stats.totalInvested !== undefined) {
      return myPlansData.stats.totalInvested;
    }
    
    return myPlansData.activePlans.reduce((total, plan) => {
      return total + (plan.investmentAmount || 0);
    }, 0);
  };

  const calculateTotalReturns = () => {
    return myPlansData.activePlans.reduce((total, plan) => {
      return total + (plan.returns?.totalExpected || plan.totalReturn || 0);
    }, 0);
  };

  const calculateHourlyEarnings = () => {
    return myPlansData.activePlans.reduce((total, plan) => {
      return total + (plan.returns?.hourlyAmount || 
                     plan.hourlyIncome || 
                     (plan.returns?.dailyAmount || plan.dailyIncomeAmount || 0) / 24 || 0);
    }, 0);
  };

  const calculateROI = () => {
    const totalInvested = calculateTotalInvested();
    const totalEarned = calculateTotalEarnings();
    
    if (totalInvested === 0) return 0;
    return (totalEarned / totalInvested) * 100;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        gap: '20px',
        backgroundColor: '#0f0f0f'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 212, 255, 0.2)',
          borderTop: '4px solid #00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#94a3b8' }}>Loading dashboard...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const totalEarnings = calculateTotalEarnings();
  const totalInvested = calculateTotalInvested();
  const totalReturns = calculateTotalReturns();
  const hourlyEarnings = calculateHourlyEarnings();
  const dailyIncome = todayEarnings;
  const roi = calculateROI();
  const activePlansCount = myPlansData.activePlans.length;

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      
      {/* UPDATE NOTIFICATION BANNER */}
      {showUpdateBanner && (
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e, #2d1b4e)',
          border: '1px solid rgba(255, 200, 0, 0.3)',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          boxShadow: '0 4px 20px rgba(255, 200, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              fontSize: '24px',
              animation: 'pulse 2s infinite'
            }}>
              🚧
            </div>
            <div>
              <p style={{
                color: '#ffd700',
                fontWeight: '600',
                margin: '0 0 4px 0',
                fontSize: '15px'
              }}>
                System Update in Progress
              </p>
              <p style={{
                color: '#94a3b8',
                margin: 0,
                fontSize: '13px'
              }}>
                We are currently updating our systems. You may experience minor technical issues or delays during this time. Your earnings are being calculated in real-time.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowUpdateBanner(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#94a3b8',
              padding: '4px 12px',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            Dismiss ✕
          </button>
        </div>
      )}

      {/* Header Card - Dark Theme */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '20px',
        padding: '24px',
        color: 'white',
        marginBottom: '24px',
        boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)',
        border: '1px solid rgba(0, 212, 255, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 8px 0',
              background: 'linear-gradient(135deg, #fff, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Welcome Back, {profile?.user?.username || 'User'}!
            </h1>
            <p style={{
              opacity: '0.7',
              margin: 0,
              fontSize: '14px'
            }}>
              Track your investments and earnings
            </p>
          </div>
          <div style={{
            background: 'rgba(0, 212, 255, 0.2)',
            borderRadius: '50%',
            padding: '12px',
            fontSize: '24px',
            border: '1px solid rgba(0, 212, 255, 0.5)'
          }}>
            👤
          </div>
        </div>
        
        <div style={{
          background: 'rgba(0, 212, 255, 0.1)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <p style={{
            fontSize: '14px',
            opacity: '0.7',
            margin: '0 0 8px 0'
          }}>Available Balance</p>
          <p style={{
            fontSize: '36px',
            fontWeight: '700',
            margin: 0,
            color: '#00d4ff'
          }}>${balance.toFixed(2)}</p>
        </div>
      </div>

      {/* Stats Grid - Dark Theme Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        {[
          { title: 'Total Balance', value: `$${balance.toFixed(2)}`, icon: '💰', color: '#00d4ff', desc: 'Available funds' },
          { title: 'Total Earned', value: `$${totalEarnings.toFixed(2)}`, icon: '💵', color: '#10b981', desc: 'From all investments' },
          { title: "Today's Earnings", value: `$${dailyIncome.toFixed(2)}`, icon: '☀️', color: '#f59e0b', desc: 'Estimated today' },
          { title: 'Active Plans', value: activePlansCount, icon: '📈', color: '#8b5cf6', desc: 'Running investments' },
          { title: 'Hourly Income', value: `$${hourlyEarnings.toFixed(2)}`, icon: '⏰', color: '#ec489a', desc: 'Per hour earnings' },
          { title: 'Total Invested', value: `$${totalInvested.toFixed(2)}`, icon: '📊', color: '#00d4ff', desc: 'Capital invested' }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#1a1a2e',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            borderRadius: '16px',
            padding: '20px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 212, 255, 0.2)';
            e.currentTarget.style.border = '1px solid #00d4ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.2)';
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  margin: '0 0 4px 0'
                }}>{stat.title}</p>
                <p style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  margin: '0 0 8px 0',
                  color: stat.color
                }}>{stat.value}</p>
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: 0
                }}>{stat.desc}</p>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'rgba(0, 212, 255, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Earnings Summary - Dark Theme */}
      <div style={{
        background: '#1a1a2e',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '32px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            margin: 0,
            color: '#00d4ff'
          }}>Earnings Summary</h2>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px'
        }}>
          {[
            { title: 'Total Returns', value: `$${totalReturns.toFixed(2)}`, icon: '💵', desc: 'Expected total returns', color: '#10b981' },
            { title: 'Daily Income', value: `$${dailyIncome.toFixed(2)}`, icon: '☀️', desc: 'Estimated daily earnings', color: '#f59e0b' },
            { title: 'Hourly Rate', value: `$${hourlyEarnings.toFixed(2)}`, icon: '⏰', desc: 'Earnings per hour', color: '#ec489a' },
            { title: 'ROI', value: `${roi.toFixed(2)}%`, icon: '📈', desc: 'Return on investment', color: '#00d4ff' }
          ].map((item, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              background: '#0f0f0f',
              border: '1px solid rgba(0, 212, 255, 0.15)',
              borderRadius: '12px',
              padding: '20px',
              transition: 'all 0.2s'
            }}>
              <div style={{
                background: '#1a1a2e',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '24px',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                {item.icon}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '14px',
                  color: '#94a3b8',
                  margin: '0 0 4px 0'
                }}>{item.title}</h3>
                <p style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: item.color,
                  margin: '0 0 4px 0'
                }}>{item.value}</p>
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: 0
                }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Investment Management - Dark Theme */}
      <div style={{
        background: '#1a1a2e',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '32px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            margin: 0,
            color: '#00d4ff'
          }}>Investment Management</h2>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          <Link to="/myplans" style={{
            display: 'flex',
            alignItems: 'center',
            background: '#0f0f0f',
            border: '1px solid rgba(0, 212, 255, 0.15)',
            borderRadius: '12px',
            padding: '20px',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.border = '1px solid #00d4ff';
            e.currentTarget.style.transform = 'translateX(5px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}>
            <div style={{
              background: '#1a1a2e',
              borderRadius: '10px',
              padding: '12px',
              marginRight: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              fontSize: '24px'
            }}>📈</div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#00d4ff',
                margin: '0 0 4px 0'
              }}>My Plans</h3>
              <p style={{
                fontSize: '13px',
                color: '#94a3b8',
                margin: '0 0 8px 0'
              }}>View all your active and completed investment plans</p>
              <div style={{
                display: 'inline-block',
                background: 'rgba(0, 212, 255, 0.2)',
                color: '#00d4ff',
                fontSize: '12px',
                fontWeight: '500',
                padding: '2px 8px',
                borderRadius: '12px'
              }}>
                {activePlansCount} active plan{activePlansCount !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ fontSize: '20px', color: '#00d4ff' }}>→</div>
          </Link>
          
          <Link to="/plans" style={{
            display: 'flex',
            alignItems: 'center',
            background: '#0f0f0f',
            border: '1px solid rgba(0, 212, 255, 0.15)',
            borderRadius: '12px',
            padding: '20px',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.border = '1px solid #00d4ff';
            e.currentTarget.style.transform = 'translateX(5px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}>
            <div style={{
              background: '#1a1a2e',
              borderRadius: '10px',
              padding: '12px',
              marginRight: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              fontSize: '24px'
            }}>💵</div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#00d4ff',
                margin: '0 0 4px 0'
              }}>Browse Plans</h3>
              <p style={{
                fontSize: '13px',
                color: '#94a3b8',
                margin: 0
              }}>Explore new investment opportunities</p>
            </div>
            <div style={{ fontSize: '20px', color: '#00d4ff' }}>→</div>
          </Link>
          
          <Link to="/withdraw" style={{
            display: 'flex',
            alignItems: 'center',
            background: '#0f0f0f',
            border: '1px solid rgba(0, 212, 255, 0.15)',
            borderRadius: '12px',
            padding: '20px',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.border = '1px solid #00d4ff';
            e.currentTarget.style.transform = 'translateX(5px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}>
            <div style={{
              background: '#1a1a2e',
              borderRadius: '10px',
              padding: '12px',
              marginRight: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              fontSize: '24px'
            }}>⬆️</div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#00d4ff',
                margin: '0 0 4px 0'
              }}>Withdraw Earnings</h3>
              <p style={{
                fontSize: '13px',
                color: '#94a3b8',
                margin: '0 0 8px 0'
              }}>Withdraw your earnings to your wallet</p>
              <div style={{
                display: 'inline-block',
                background: 'rgba(0, 212, 255, 0.2)',
                color: '#00d4ff',
                fontSize: '12px',
                fontWeight: '500',
                padding: '2px 8px',
                borderRadius: '12px'
              }}>
                Balance: ${balance.toFixed(2)}
              </div>
            </div>
            <div style={{ fontSize: '20px', color: '#00d4ff' }}>→</div>
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        @media (max-width: 768px) {
          .dashboard-container {
            padding-bottom: 80px;
          }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;